/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { TOAST_TYPE_DANGER, Toast, ToastProps } from '@squiz/sds';
import { useToastStore } from 'dxpConsoleCore-mf/State';
import find from 'lodash/find';
import isNull from 'lodash/isNull';
import isUndefined from 'lodash/isUndefined';
import property from 'lodash/property';
import uniq from 'lodash/uniq';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { lastValueFrom } from 'rxjs/internal/lastValueFrom';
import { CDP_STRINGS } from '../../../../../Constants/Strings.constants';
import { SegmentGroupSwitcherOperator } from '../../../../../Screens/SegmentBuilder/elements/SegmentGroupSwitcher/SegmentGroupSwitcher.types';
import { SEGMENT_BUILDER_DATA_IDS } from '../../../../../Screens/SegmentBuilder/SegmentBuilder.constants';
import {
  SegmentationCreateResponse,
  SegmentationGroupCreateResponse,
  SegmentationMessageResponse,
  SegmentationServiceCreateGroupDto,
  SegmentationServiceCreateSegmentDto,
  SegmentationServiceDeleteSegmentDto,
  SegmentationServiceUpdateGroupDto,
  SegmentationServiceUpdateSegmentDto,
  getSegmentationService,
} from '../../../../../Services/segmentation';
import { EntitySegment, EntitySegmentGroup, EntitySegmentList } from '../../Entities/entities';
import { UseSegmentFormDto, UseSegmentFormResponse } from './UseSegmentForm.hook.defs';

/**
 * React hook that abstracts the handling of segment updates and creation.
 * @export
 * @returns {UseSegmentFormResponse} The use segment form response.
 */
export function useSegmentForm(dto: UseSegmentFormDto): UseSegmentFormResponse {
  const { groups, segmentId } = dto;
  const [segmentChanges, setSegments] = useState<EntitySegmentList | null>(null);
  const [removedSegments, setRemovedSegments] = useState<Array<string>>([]);
  const { updateActiveToast } = useToastStore();
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const { t } = useTranslation();

  // Segmentation service
  const segmentationService = getSegmentationService();

  // Labels
  const labels = useMemo(() => {
    return {
      addRulesetLabel: t(CDP_STRINGS.addRulesetLabel),
      createSegmentText: t(CDP_STRINGS.segmentBuilderCreateButton),
      detailsSectionName: t(CDP_STRINGS.segmentBuilderDetailsSection),
      errorCountNumber: t(CDP_STRINGS.errorCountNumber),
      idFieldErrorMessageEmpty: t(CDP_STRINGS.idFieldErrorMessageEmpty),
      idFieldErrorMessagePattern: t(CDP_STRINGS.idFieldErrorMessagePattern),
      nameFieldErrorMessageEmpty: t(CDP_STRINGS.nameFieldErrorMessageEmpty),
      requiredAttribute: t(CDP_STRINGS.requiredAttribute),
      requiredEventFeed: t(CDP_STRINGS.requiredEventFeed),
      requiredNumber: t(CDP_STRINGS.requiredNumber),
      requiredRule: t(CDP_STRINGS.requiredRule),
      requiredValue: t(CDP_STRINGS.requiredValue),
      ruleButtonText: t(CDP_STRINGS.ruleButtonTextAdd),
      rulesetButtonTextAdd: t(CDP_STRINGS.rulesetButtonTextAdd),
      rulesetButtonTextDelete: t(CDP_STRINGS.rulesetButtonTextDelete),
      rulesetTitleDataIntegration: t(CDP_STRINGS.rulesetTitleDataIntegration),
      rulesetTitleWebsiteBehavior: t(CDP_STRINGS.rulesetTitleWebsiteBehavior),
      rulesSectionName: t(CDP_STRINGS.segmentBuilderRulesSection),
      saveSegmentText: t(CDP_STRINGS.saveChanges),
      toastErrorMessage: t(CDP_STRINGS.toastErrorLabel),
      toastSuccessCreateMessage: t(CDP_STRINGS.toastSuccessCreateMessage),
      toastSuccessCreateMessageEnd: t(CDP_STRINGS.toastSuccessCreateMessageEnd),
      toastSuccessCreateTitle: t(CDP_STRINGS.toastSuccessCreateLabel),
      toastSuccessUpdateMessage: t(CDP_STRINGS.toastSuccessUpdateLabel),
    };
  }, [t]);

  // If the segment belongs to a group.
  const groupId = useMemo((): null | string => {
    if (!segmentId || !groups) {
      return null;
    }

    let gid = null;
    Object.entries(groups).forEach(([id, data]) => {
      if (data.segments.includes(segmentId)) {
        gid = id;
      }
    });

    return gid;
  }, [groups, segmentId]);

  /**
   * Adds segments to the changed segment list.
   */
  const addSegments = useCallback((segmentsList: EntitySegmentList): void => {
    setSegments((currentSegments) => {
      if (currentSegments === null) {
        return segmentsList;
      } else {
        return [...currentSegments, ...segmentsList];
      }
    });
  }, []);

  /**
   * Update an existing segment.
   */
  const updateSegment = useCallback(
    (changedSegment: EntitySegment): void => {
      setSegments((existingSegments): EntitySegmentList => {
        if (existingSegments && find(existingSegments, (segment) => segment.internalId === changedSegment.internalId)) {
          return existingSegments.map((segment: EntitySegment): EntitySegment => {
            if (changedSegment.internalId === segment.internalId) {
              return changedSegment;
            }
            return segment;
          });
        } else {
          // Add the segment if it doesn't exist.
          return [...(existingSegments || []), changedSegment];
        }
      });
    },
    [setSegments],
  );

  /**
   * Update an existing segment.
   */
  const removeSegment = useCallback((removedSegmentId: string): void => {
    setSegments((currentSegments) => {
      return (currentSegments as Array<EntitySegment>).filter((segment) => segment.internalId !== removedSegmentId);
    });

    if (!removedSegmentId.includes(`tmp-`)) {
      setRemovedSegments((currentSegments) => {
        return uniq([...currentSegments, removedSegmentId]);
      });
    }
  }, []);

  // Error toast props
  const errorToastProps: ToastProps = useMemo(() => {
    return {
      showToast: true,
      stateHandler: useToastStore,
      toastContent: <strong>{labels.toastErrorMessage}</strong>,
      toastType: TOAST_TYPE_DANGER,
    };
  }, [labels.toastErrorMessage]);

  const sendUpdateSegmentRequest = useCallback(
    (segmentId: string, segment: EntitySegment): Promise<SegmentationMessageResponse | null> => {
      // Prepare the DTO
      const dto: SegmentationServiceUpdateSegmentDto = {
        segment,
        segmentId,
      };

      return lastValueFrom(segmentationService.updateSegment(dto));
    },
    [segmentationService],
  );

  const sendCreateSegmentRequest = useCallback(
    (segment: EntitySegment): Promise<SegmentationCreateResponse | null> => {
      // Prepare the DTO
      const dto: SegmentationServiceCreateSegmentDto = {
        segment,
      };

      return lastValueFrom(segmentationService.createSegment(dto));
    },
    [segmentationService],
  );

  const sendDeleteSegmentRequest = useCallback(
    (dto: SegmentationServiceDeleteSegmentDto): Promise<SegmentationMessageResponse | null> => {
      return lastValueFrom(segmentationService.deleteSegment(dto));
    },
    [segmentationService],
  );

  const deleteAllRemovedSegments = useCallback(async () => {
    await Promise.all(removedSegments.map((segmentId) => sendDeleteSegmentRequest({ segmentId })));
    // Reset removed segments.
    setRemovedSegments([]);
  }, [removedSegments, sendDeleteSegmentRequest, setRemovedSegments]);

  const sendCreateGroupRequest = useCallback(
    (group: EntitySegmentGroup): Promise<SegmentationGroupCreateResponse | null> => {
      // Prepare the DTO
      const dto: SegmentationServiceCreateGroupDto = {
        group,
      };

      return lastValueFrom(segmentationService.createGroup(dto));
    },
    [segmentationService],
  );

  const sendUpdateGroupRequest = useCallback(
    (groupId: string, group: EntitySegmentGroup): Promise<SegmentationMessageResponse | null> => {
      // Prepare the DTO
      const dto: SegmentationServiceUpdateGroupDto = {
        group,
        groupId,
      };

      return lastValueFrom(segmentationService.updateGroup(dto));
    },
    [segmentationService],
  );

  /**
   * Prepare a segment for an api request by removing any non api values.
   */
  const prepareSegmentForRequest = useCallback((segment: EntitySegment): EntitySegment => {
    const seg = { ...segment };
    delete seg.internalId;
    return seg;
  }, []);

  /**
   * Save segment changes to the current segment list.
   */
  const saveSegments = useCallback(
    async (nextGroup: Partial<EntitySegmentGroup>): Promise<void> => {
      if (segmentChanges) {
        const responses = [];
        const tmpSegmentIdMap: Record<string, string> = {};
        setIsSaving(true);

        try {
          for await (const segment of segmentChanges) {
            if (segment) {
              if (isUndefined(segment.internalId) || segment.internalId.includes(`tmp-`)) {
                const createResponse = await sendCreateSegmentRequest(prepareSegmentForRequest(segment));
                responses.push(createResponse);
                // Map the temporary internal id to actual segment id.
                tmpSegmentIdMap[`${segment.internalId}`] = `${createResponse?.segmentid}`;
              } else {
                responses.push(await sendUpdateSegmentRequest(segment.internalId, prepareSegmentForRequest(segment)));
              }
            }
          }

          // Skip groups if no group name exists.
          if (!nextGroup.name) {
            // eslint-disable-next-line promise/no-return-wrap
            return Promise.resolve();
          }
          // Merge new segment ids from responses to existing segment ids to form a list of segments that
          // belong to the group being created or updated.
          const newSegmentIds: Array<string> = (
            responses as Array<SegmentationCreateResponse | SegmentationMessageResponse>
          )
            .filter((response) => !isNull(response))
            .map((response): string => (response as SegmentationCreateResponse).segmentid as string) as Array<string>;
          const existingSegmentIds: Array<string> = segmentChanges
            .filter((segment) => !segment.internalId?.startsWith(`tmp-`))
            .map((segment) => segment.internalId) as Array<string>;
          const segmentIds = uniq([...newSegmentIds, ...existingSegmentIds]) as Array<string>;

          // Existing group.
          if (groupId) {
            const group = property(groupId)(groups) as EntitySegmentGroup;
            if (!group) {
              throw new Error(`Unable to determine existing group to save.`);
            }
            const groupData = {
              ...group,
              ...nextGroup,
              segments: segmentIds,
            };

            // Update priority ids with real segment ids if they exist.
            groupData.priority = groupData.priority.map((id) => {
              if (tmpSegmentIdMap[id]) {
                return tmpSegmentIdMap[id];
              }
              return id;
            });
            await sendUpdateGroupRequest(groupId, groupData);
          } else {
            // New group.
            await sendCreateGroupRequest({
              description: ``,
              name: nextGroup.name,
              operator: nextGroup.operator || SegmentGroupSwitcherOperator.ByRecency,
              // Note: at this point for newly created groups priority will be set.
              priority: (nextGroup.priority as Array<string>).map((id) => {
                if (tmpSegmentIdMap[id]) {
                  return tmpSegmentIdMap[id];
                }
                return id;
              }),
              segments: segmentIds,
            });
          }

          await deleteAllRemovedSegments();

          setIsSaving(false);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error(err);
          updateActiveToast(<Toast {...errorToastProps} data-id={SEGMENT_BUILDER_DATA_IDS.toastFailCreate} />);
          setIsSaving(false);
        }
      }
    },
    [
      deleteAllRemovedSegments,
      errorToastProps,
      groupId,
      groups,
      prepareSegmentForRequest,
      segmentChanges,
      sendCreateGroupRequest,
      sendCreateSegmentRequest,
      sendUpdateGroupRequest,
      sendUpdateSegmentRequest,
      updateActiveToast,
    ],
  );

  return {
    addSegments,
    groupId,
    isSaving,
    removedSegments,
    removeSegment,
    saveSegments,
    segmentChanges,
    setSegments,
    updateSegment,
  };
}
